'use client';

import { useQuery } from '@tanstack/react-query';

import {
  getCourseStructureByIdService,
  getCourseStructuresListService,
} from '@/services/httpServices/AutocourseServices';
import type {
  CourseStructureListItem,
  GetAutocourseListServiceConfig,
  GetAutocourseSingleByIdServiceConfig,
} from '@/services/httpServices/AutocourseServices/types';

import { autocourseByIdKeys, autocourseListKeys } from './autocourseQueryKeys';
import { useQueryConfig } from './useQueryConfig';

// Document analysis / course generation are the only non-terminal states; a row
// in either is still being processed by the AI and its status will change on a
// later fetch. `courseStructureGenerated` and `error` are terminal.
const IN_PROGRESS_STATUSES = ['analyzingDocument', 'generatingCourse'];

// Poll every 5s while at least one row is still processing, then stop. Generation
// routinely outlasts a fixed two-shot poll, so a frozen "analyzing document…"
// chip read as stuck and forced a manual refresh.
const AUTOCOURSE_POLL_INTERVAL_MS = 5000;

function hasInProgressRow(nodes: CourseStructureListItem[]) {
  return nodes.some(node => IN_PROGRESS_STATUSES.includes(node.status));
}

export function useAutocourseQueryList(
  params: GetAutocourseListServiceConfig,
  options?: { enabled?: boolean },
) {
  const { isEnabled } = useQueryConfig();
  const enabled = isEnabled && options?.enabled !== false;

  return useQuery({
    queryKey: autocourseListKeys(params),
    queryFn: async () => getCourseStructuresListService(params),
    staleTime: 60000,
    enabled,
    // Keep refetching only while a visible row is still processing; once every
    // row is terminal the interval returns false and polling stops.
    refetchInterval: (query) => {
      const nodes = query.state.data?.nodes ?? [];
      return hasInProgressRow(nodes) ? AUTOCOURSE_POLL_INTERVAL_MS : false;
    },
    refetchOnWindowFocus: true,
  });
}

export function useAutocourseById(params: GetAutocourseSingleByIdServiceConfig) {
  const { isEnabled } = useQueryConfig();

  return useQuery({
    queryKey: autocourseByIdKeys(params),
    queryFn: async () => getCourseStructureByIdService(params),
    enabled: isEnabled,
  });
}
